import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ShieldAlert,
  Users,
  Globe,
  Clock,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Ban,
  XCircle,
} from 'lucide-react';
import { Card, Badge, Button, StatCard, Modal, ModalFooter, Textarea } from '@azyntor-labs/nexus-ui';
import { attemptsApi } from '@/api/endpoints';
import type { SuspiciousCluster, SuspiciousIPPair, SuspiciousMember } from '@/api/endpoints';
import { toast } from '@/utils/toast';

function fmtDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return iso;
  }
}

function severity(match: number): 'danger' | 'warning' | 'info' {
  if (match >= 95) return 'danger';
  if (match >= 80) return 'warning';
  return 'info';
}

function MemberRow({ m }: { m: SuspiciousMember }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center text-sm py-2 px-3 hover:bg-white/[0.02] rounded">
      <div className="col-span-4 min-w-0">
        <div className="text-white/90 font-medium truncate">{m.student_name || '—'}</div>
        <div className="text-xs text-white/40 truncate">{m.student_email}</div>
      </div>
      <div className="col-span-2 text-xs text-white/60 font-mono">{m.ip_address || '—'}</div>
      <div className="col-span-2 text-xs">
        {typeof m.percentage === 'number' ? (
          <span className={m.result === 'pass' ? 'text-emerald-300' : 'text-white/70'}>
            {m.percentage.toFixed(1)}%
          </span>
        ) : (
          <span className="text-white/40">—</span>
        )}
        {m.result && (
          <span className="ml-1 text-[10px] uppercase tracking-wider text-white/40">{m.result}</span>
        )}
      </div>
      <div className="col-span-3 text-xs text-white/50">{fmtDate(m.start_time)}</div>
      <div className="col-span-1 text-right">
        <Link
          to={`/teacher/exam-results/attempt/${m.attempt_id}`}
          className="inline-flex items-center gap-1 text-teal-300 hover:text-teal-200 text-xs"
        >
          View <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function ClusterCard({
  cluster,
  onInvalidate,
  invalidating,
}: {
  cluster: SuspiciousCluster;
  onInvalidate: (c: SuspiciousCluster) => void;
  invalidating: boolean;
}) {
  const tone = severity(cluster.match_percentage);
  const borderTone =
    tone === 'danger'
      ? 'border-red-500/40'
      : tone === 'warning'
      ? 'border-amber-500/40'
      : 'border-sky-500/40';

  const hasSmokingGun = (cluster.same_wrong_answers?.length ?? 0) > 0;

  return (
    <Card className={`border ${borderTone}`}>
      <div className="p-5 border-b border-white/5 flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ShieldAlert className="w-4 h-4 text-amber-400" />
            <h3 className="text-base font-semibold text-white">
              Cluster of {cluster.members.length} students
            </h3>
            <Badge variant={tone} size="sm">
              {cluster.match_percentage}% match
            </Badge>
            {hasSmokingGun && (
              <Badge variant="danger" size="sm">
                <XCircle className="w-3 h-3 mr-1" />
                Smoking gun
              </Badge>
            )}
          </div>
          <p className="text-xs text-white/50">
            {cluster.identical_questions} identical / {cluster.compared_questions} compared questions
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          {cluster.signals.map((s, i) => (
            <Badge key={i} variant="secondary" size="sm">
              {s}
            </Badge>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onInvalidate(cluster)}
            disabled={invalidating}
            isLoading={invalidating}
            leftIcon={<Ban className="w-3.5 h-3.5" />}
            className="!text-red-300 !border-red-500/40 hover:!bg-red-500/10 ml-1"
          >
            Invalidate all
          </Button>
        </div>
      </div>

      {/* Same-wrong-answer diagnostic */}
      {hasSmokingGun && (
        <div className="p-4 border-b border-red-500/20 bg-red-500/[0.04]">
          <p className="text-[10px] uppercase tracking-wider text-red-300/80 mb-2 inline-flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Same wrong answer across all members —{' '}
            {cluster.same_wrong_answers!.length} question
            {cluster.same_wrong_answers!.length === 1 ? '' : 's'}
          </p>
          <div className="space-y-2">
            {cluster.same_wrong_answers!.map((w) => (
              <div
                key={w.question_id}
                className="text-xs rounded border border-red-500/20 bg-black/20 px-3 py-2"
              >
                <div className="text-white/80 line-clamp-2">{w.question_text}</div>
                <div className="text-red-300 mt-1">
                  all picked:{' '}
                  <span className="font-medium text-red-200">
                    {w.picked_label || w.picked_raw || '(empty)'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-3">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-white/40 px-3 py-1">
          <div className="col-span-4">Student</div>
          <div className="col-span-2">IP</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-3">Submitted</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {cluster.members.map((m) => (
            <MemberRow key={m.attempt_id} m={m} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function IPPairCard({ pair }: { pair: SuspiciousIPPair }) {
  return (
    <Card className="border border-amber-500/20">
      <div className="p-4 border-b border-white/5 flex items-center gap-2">
        <Globe className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-white">
          Shared IP: <span className="font-mono">{pair.ip_address}</span>
        </h3>
        <span className="text-xs text-white/40">
          {pair.members.length} attempt{pair.members.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="p-3">
        <div className="divide-y divide-white/[0.04]">
          {pair.members.map((m) => (
            <MemberRow key={m.attempt_id} m={m} />
          ))}
        </div>
      </div>
    </Card>
  );
}

export const SuspiciousAttemptsPage = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [invalidateTarget, setInvalidateTarget] = useState<SuspiciousCluster | null>(null);
  const [invalidateReason, setInvalidateReason] = useState('');

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['suspicious-attempts', examId],
    queryFn: () => attemptsApi.scanSuspicious(examId!),
    enabled: !!examId,
    staleTime: 60_000,
  });

  const report = data?.data?.data;

  const bulkCancelMutation = useMutation({
    mutationFn: ({ attemptIds, reason }: { attemptIds: string[]; reason: string }) =>
      attemptsApi.bulkCancel(attemptIds, reason),
    onSuccess: (res) => {
      const { cancelled, total, results } = res.data?.data || { cancelled: 0, total: 0, results: [] };
      if (cancelled === total) {
        toast.success(`Invalidated all ${cancelled} attempt(s) in the cluster`);
      } else {
        const failed = results.filter((r) => !r.cancelled);
        toast.warning(
          `Invalidated ${cancelled} of ${total}. ${failed.length} failed — likely graded or already cancelled.`,
        );
      }
      setInvalidateTarget(null);
      setInvalidateReason('');
      queryClient.invalidateQueries({ queryKey: ['suspicious-attempts', examId] });
    },
    onError: (err: unknown) => {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(anyErr?.response?.data?.message || anyErr?.message || 'Bulk cancel failed');
    },
  });
  const errMessage =
    (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
    (error as { message?: string })?.message;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(`/teacher/exam-results/exam/${examId}`)}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-400" />
              Suspicious attempts
            </h1>
            <p className="text-sm text-white/50 mt-0.5 truncate">
              {report?.exam_title || 'Scanning…'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Rescan
        </Button>
      </div>

      {/* Summary stats */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            title="Scanned"
            value={String(report.scanned_attempts)}
            icon={<Users className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Clusters"
            value={String(report.clusters.length)}
            icon={<ShieldAlert className="w-4 h-4" />}
            color={report.clusters.length > 0 ? 'red' : 'green'}
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Shared-IP pairs"
            value={String(report.ip_only_pairs.length)}
            icon={<Globe className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Threshold"
            value={`${report.threshold}%`}
            icon={<Clock className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <Card>
          <div className="p-8 text-center text-white/50 text-sm">Scanning attempts…</div>
        </Card>
      )}

      {/* Error */}
      {!isLoading && errMessage && (
        <Card className="border border-red-500/30">
          <div className="p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-300">Scan failed</p>
              <p className="text-xs text-white/60 mt-1">{errMessage}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {report && report.clusters.length === 0 && report.ip_only_pairs.length === 0 && (
        <Card className="border border-emerald-500/20">
          <div className="p-8 text-center space-y-2">
            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto" />
            <p className="text-white/90 font-medium">No suspicious patterns detected</p>
            <p className="text-xs text-white/50">
              Scanned {report.scanned_attempts} attempt{report.scanned_attempts === 1 ? '' : 's'} · threshold{' '}
              {report.threshold}% answer match
            </p>
          </div>
        </Card>
      )}

      {/* Clusters */}
      {report && report.clusters.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            Answer-similarity clusters ({report.clusters.length})
          </h2>
          <div className="space-y-3">
            {report.clusters.map((c) => (
              <ClusterCard
                key={c.id}
                cluster={c}
                onInvalidate={(cluster) => {
                  setInvalidateTarget(cluster);
                  setInvalidateReason('');
                }}
                invalidating={
                  bulkCancelMutation.isPending &&
                  invalidateTarget?.id === c.id
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* IP-only pairs */}
      {report && report.ip_only_pairs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Shared-IP pairs ({report.ip_only_pairs.length})
          </h2>
          <p className="text-xs text-white/40 -mt-1">
            Attempts from the same IP whose answers did NOT cross the {report?.threshold}% similarity threshold.
            Review for proctoring context.
          </p>
          <div className="space-y-3">
            {report.ip_only_pairs.map((p) => (
              <IPPairCard key={p.ip_address} pair={p} />
            ))}
          </div>
        </div>
      )}

      {/* Footer meta */}
      {report && (
        <p className="text-xs text-white/30 text-right">
          Generated {fmtDate(report.generated_at)}
        </p>
      )}

      {/* Bulk invalidate confirmation */}
      <Modal
        isOpen={!!invalidateTarget}
        onClose={() => {
          if (!bulkCancelMutation.isPending) {
            setInvalidateTarget(null);
            setInvalidateReason('');
          }
        }}
        title="Invalidate this cluster?"
        subtitle={
          invalidateTarget
            ? `Cancels ${invalidateTarget.members.length} attempts so they no longer count toward each student's quota and won't appear in results. Already-graded attempts will be skipped.`
            : ''
        }
        size="md"
      >
        <div className="space-y-3">
          {invalidateTarget && (
            <div className="rounded border border-white/10 bg-black/20 p-3 text-xs text-white/70 max-h-48 overflow-y-auto">
              {invalidateTarget.members.map((m) => (
                <div key={m.attempt_id} className="flex items-center justify-between py-0.5">
                  <span className="truncate">{m.student_name || m.student_email}</span>
                  <span className="text-white/30 text-[10px] font-mono">{m.attempt_id.slice(0, 8)}</span>
                </div>
              ))}
            </div>
          )}
          <Textarea
            label="Reason (required, written to audit log)"
            placeholder="e.g. Coordinated cheating — CEH V13 scholarship cluster confirmed"
            value={invalidateReason}
            onChange={(e) => setInvalidateReason(e.target.value)}
            rows={3}
          />
          <p className="text-xs text-white/40">
            Each cancellation is logged with your user ID.
          </p>
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setInvalidateTarget(null);
              setInvalidateReason('');
            }}
            disabled={bulkCancelMutation.isPending}
          >
            Keep attempts
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!invalidateTarget) return;
              bulkCancelMutation.mutate({
                attemptIds: invalidateTarget.members.map((m) => m.attempt_id),
                reason: invalidateReason.trim(),
              });
            }}
            disabled={!invalidateReason.trim() || bulkCancelMutation.isPending}
            isLoading={bulkCancelMutation.isPending}
          >
            Invalidate {invalidateTarget?.members.length ?? 0} attempts
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
